import { useCallback, useRef, useState } from 'react';
import { Outlet } from 'react-router';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { Footer } from './Footer';
import { ScrollRevealFooter } from './ScrollRevealFooter';
import { useFooterReveal } from './useFooterReveal';
import { useRealtimeInvalidation } from '@core/realtime/useRealtimeInvalidation';
import { useLiveQuotesListener } from '@features/quotes/useLiveQuotesListener';
import { useCqgGapFillResync } from '@features/cqg/hooks/useCqgGapFillResync';
import { AccountInitializer } from '@shared/state/AccountInitializer';
import { CqgAutoSwitch } from '@shared/state/CqgAutoSwitch';
import { UserTimeZoneSync } from '@shared/state/UserTimeZoneSync';
import { CqgConnectBanner, CqgConnectedPopup, CqgReconnectPopup } from '@features/cqg';
import { useAccountLoginRecorder } from '@features/accounts/hooks/useAccountLoginRecorder';

export function AppShell() {
  useRealtimeInvalidation();
  useLiveQuotesListener();
  /** Reveal-on-scroll footer: shown while the page scrolls down, hidden on any scroll up and at the top. */
  const footer = useFooterReveal();
  /** The overlay footer's measured height — the spacer below the page content matches it, so the
   *  very bottom of long content can always scroll clear of the overlay, and short pages gain
   *  exactly enough scroll room for the footer to be reachable at all. */
  const [footerHeight, setFooterHeight] = useState(0);
  const footerObserver = useRef<ResizeObserver | null>(null);
  const footerRef = useCallback((node: HTMLDivElement | null) => {
    footerObserver.current?.disconnect();
    footerObserver.current = null;
    if (node && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => setFooterHeight(node.offsetHeight));
      ro.observe(node);
      footerObserver.current = ro;
      setFooterHeight(node.offsetHeight);
    }
  }, []);
  // Remembers, per CQG account, the username+environment that connected it (FE-only localStorage)
  // so the Accounts panel can prefill the dialog for that account. See useAccountLoginRecorder.
  useAccountLoginRecorder();
  // While CQG is live, re-pull the last 2h of fills every 15 min (idempotent) so any fill lost to a
  // dropped relay frame is backfilled without user action — every 30 minutes, the trader's whole
  // business day, from the tab that owns the CQG socket. See useCqgGapFillResync.
  useCqgGapFillResync();
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-canvas text-text">
      {/* Auto-picks the first account once /api/v1/trades/accounts resolves. Mounted here
          (post-AuthGuard) so the query carries a valid bearer token; mounting in AppProviders
          would fire it before login and get 401s. */}
      <AccountInitializer />
      {/* Keeps the display timezone (@shared/lib/userTz) synced to the profile's timeZone so every
          timestamp renders in the USER's timezone, not the browser's. */}
      <UserTimeZoneSync />
      {/* §14i — flips the global account to the CQG account on the Disconnected→Live transition
          so connecting a broker immediately scopes the dashboard to that book. */}
      <CqgAutoSwitch />
      <Sidebar />
      {/* `relative`: the ScrollRevealFooter overlay anchors to THIS column, so it spans the content
          area (not the sidebar) and `overflow-hidden` clips it while it is slid below the edge. */}
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <Topbar />
        {/* Full-bleed CQG onboarding banner, flush under the top bar (first-time only; renders null
            once connected/dismissed — BE-backed action flags). */}
        <CqgConnectBanner />
        {/* "Your CQG account is connected" — the 48h concurrent-login popup after a user-initiated
            connect (armed by the Account Sync page / the dialog's Connect; never on auto-resume). */}
        <CqgConnectedPopup />
        {/* Refresh-resume take-over confirm: if reconnecting a prior session needs to take over the
            user's other CQG session (concurrent_session), ask them to confirm the reconnect here. */}
        <CqgReconnectPopup />
        {/* THE FOOTER (2026-08-05, the user's actual spec after two in-flow attempts): "when we
            scroll down the footer will come up in an animation, which means we don't have to scroll
            until the bottom to see it. If no scroll, we don't see the footer."

            So the footer is NOT in the document flow at all — it is an overlay pinned to the bottom
            of this column (relative, below), hidden while the page sits at the top and slid up by
            `ScrollRevealFooter` the moment `<main>` scrolls. Mid-scroll on a long dashboard the
            Terms/Privacy links are right there; at the top of any page the vertical space is the
            content's. Since 2026-09-30 ("removed with the first scroll up at any given time") the first
            scroll up slides it away again; the next scroll down brings it back (`useFooterReveal`).

            The measured SPACER stands where the footer used to: it gives short pages exactly enough
            scroll room for the footer to be reachable at all, and lets the very bottom of long
            content scroll clear of the overlay. The page wrapper keeps `min-h-full flex-col` (the
            measured 2026-08-05 recipe): the content floor stays, flex-1 page roots fill the
            viewport, and full-bleed tricks (-mt-6/-mr-6) bleed the wrapper's padding.

            NO bottom padding (2026-09-23 user request: "remove the margin at the bottom of the
            pages, just let the content go until the bottom, it's weird to see it cut"): a flex-1
            page root now runs to the window's bottom edge instead of stopping 24px short. */}
        <main
          className="flex-1 overflow-y-auto"
          onScroll={footer.onScroll}
        >
          <div className="flex min-h-full flex-col px-6 pt-6">
            <Outlet />
          </div>
          <div aria-hidden style={{ height: footerHeight }} />
        </main>
        <ScrollRevealFooter visible={footer.visible}>
          <div ref={footerRef}>
            <Footer />
          </div>
        </ScrollRevealFooter>
        {/* The floating bottom-right Rithmic/OMNE badge was REMOVED 2026-07-25 (user direction) — the
            conformance marks now render in the Topbar (under the page title) and in the Footer. */}
      </div>
    </div>
  );
}
